import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Button,
  ButtonVariants,
  ButtonSizes,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
  TypographyWeights,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { notification } from '@/lib/notifications';
import { ConfirmDialog, EmptyState, LoadingOverlay } from '@/components/feedback';
import { ROUTES } from '@/app/routes';
import { UploadStep } from '../components/UploadStep';
import { SummaryStep } from '../components/SummaryStep';
import { ShipmentContextBar } from '../components/ShipmentContextBar';
import { addFileEntries, markTakenFiles } from '../utils/declarationsUtils';
import { selectUploadableFiles } from '../mappers/declarationsMappers';
import { AddDeclarationStep } from '../constants/declarations.constants';
import {
  useGetShipmentForAddDeclarationsQuery,
  useUploadDeclarationsMutation,
  useAddDeclarationsToShipmentMutation,
  useGetTakenDeclarationFilenamesQuery,
} from '../api/declarationsApi';
import styles from './AddDeclarationPage.module.scss';

// Labels render in their own row, never inside a step.
const STEPPER_STEPS = Object.freeze([{}, {}]);

// AddDeclarationPage — 2-step upload wizard; standalone, or scoped to a `:shipmentId`.
export const AddDeclarationPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, shipmentId } = useParams();
  const [step, setStep] = useState(AddDeclarationStep.upload);
  const [files, setFiles] = useState([]);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const isForShipment = Boolean(shipmentId);

  const {
    data: shipment,
    isLoading: isShipmentLoading,
    isError: isShipmentError,
  } = useGetShipmentForAddDeclarationsQuery(shipmentId, { skip: !isForShipment });

  const [uploadDeclarations, { isLoading: isUploading }] = useUploadDeclarationsMutation();
  const [addDeclarationsToShipment, { isLoading: isAdding }] =
    useAddDeclarationsToShipmentMutation();
  const isSubmitting = isUploading || isAdding;

  const checkableNames = useMemo(
    () => files.filter((entry) => !entry.errorCode).map((entry) => entry.name),
    [files],
  );
  const { data: takenNames, isFetching: isCheckingNames } = useGetTakenDeclarationFilenamesQuery(
    checkableNames,
    { skip: step !== AddDeclarationStep.summary || checkableNames.length === 0 },
  );
  const checkedFiles = useMemo(() => markTakenFiles(files, takenNames), [files, takenNames]);

  const isDirty = files.length > 0;
  const uploadableFiles = useMemo(() => selectUploadableFiles(checkedFiles), [checkedFiles]);

  const stepLabels = useMemo(
    () => [t('ACTION_ADD_DECLARATIONS'), t('DECLARATIONS_STEP_SUMMARY')],
    [t],
  );

  const handleFilesAdded = useCallback((fileList) => {
    setFiles((prev) => addFileEntries(prev, fileList));
  }, []);

  const handleRemoveFile = useCallback((id) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const goBack = useCallback(() => {
    navigate(
      isForShipment
        ? ROUTES.incomingShipmentDetail(module, shipmentId)
        : ROUTES.declarations(module),
    );
  }, [navigate, isForShipment, module, shipmentId]);

  const handleCloseClick = useCallback(() => {
    if (isDirty) setIsDiscardModalOpen(true);
    else goBack();
  }, [isDirty, goBack]);

  const handleSave = useCallback(async () => {
    setIsDiscardModalOpen(false);
    if (uploadableFiles.length === 0) {
      notification.warning(t('DECLARATIONS_NO_VALID_FILES'));
      return;
    }
    try {
      const { created, skipped } = isForShipment
        ? await addDeclarationsToShipment({ module, shipmentId, files: uploadableFiles }).unwrap()
        : await uploadDeclarations({ module, files: uploadableFiles }).unwrap();
      const count = created.length;

      if (skipped.length > 0) {
        notification.warning(t('DECLARATIONS_UPLOAD_PARTIAL', { count, skipped: skipped.length }));
      } else {
        notification.success(t('DECLARATIONS_SUCCESS_TOAST', { count }));
      }
      goBack();
    } catch {
      // notifyApiErrors already surfaced it.
    }
  }, [
    uploadableFiles,
    isForShipment,
    addDeclarationsToShipment,
    uploadDeclarations,
    module,
    shipmentId,
    goBack,
    t,
  ]);

  const handleGoToSummary = useCallback(() => setStep(AddDeclarationStep.summary), []);
  const handleGoToUpload = useCallback(() => setStep(AddDeclarationStep.upload), []);
  const handleCancelDiscard = useCallback(() => setIsDiscardModalOpen(false), []);

  const footerRightButtons = useMemo(
    () =>
      step === AddDeclarationStep.upload ? (
        <>
          <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={handleCloseClick}>
            {t('COMMON_CANCEL')}
          </Button>
          <Button
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            disabled={files.length === 0}
            onClick={handleGoToSummary}
          >
            {t('COMMON_NEXT')}
          </Button>
        </>
      ) : (
        <>
          <Button variant={ButtonVariants.neutral} size={ButtonSizes.sm} onClick={handleCloseClick}>
            {t('COMMON_CANCEL')}
          </Button>
          <Button
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            disabled={isSubmitting || isCheckingNames || uploadableFiles.length === 0}
            onClick={handleSave}
          >
            {t('COMMON_SAVE')}
          </Button>
        </>
      ),
    [
      step,
      files.length,
      uploadableFiles.length,
      isSubmitting,
      isCheckingNames,
      handleCloseClick,
      handleSave,
      handleGoToSummary,
      t,
    ],
  );

  if (isForShipment && isShipmentLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isForShipment && (isShipmentError || !shipment)) {
    return (
      <EmptyState
        title={t('DECLARATIONS_SHIPMENT_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goBack}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <div className={styles.page}>
      <Form.Header title={t('DECLARATIONS_ADD_TITLE')} onClose={handleCloseClick} />

      <div className={styles.stepperBar}>
        <div className={styles.stepperInstance}>
          <Form.Stepper
            steps={STEPPER_STEPS}
            activeStep={step}
            // TODO: swap to StepperVariants.flush once @carboledger/ui exports it.
            variant="flush"
          />
          <div className={styles.stepLabels}>
            {stepLabels.map((label, index) => (
              <Typography
                key={label}
                as={TypographyElements.span}
                variant={TypographyVariants.bodyXs}
                weight={TypographyWeights.semibold}
                color={index === step ? TypographyColors.brand : TypographyColors.primary}
              >
                {label}
              </Typography>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.scrollArea}>
        {isForShipment && step === AddDeclarationStep.upload && (
          <>
            <Typography variant={TypographyVariants.bodySm} color={TypographyColors.secondary}>
              {t('DECLARATIONS_ADD_FOR_SHIPMENT_SUBTITLE')}
            </Typography>
            <ShipmentContextBar shipment={shipment} />
          </>
        )}

        {step === AddDeclarationStep.upload ? (
          <UploadStep
            files={files}
            onFilesAdded={handleFilesAdded}
            onRemoveFile={handleRemoveFile}
          />
        ) : (
          <SummaryStep files={checkedFiles} />
        )}
      </div>

      <Form.Footer align="between">
        <Button
          variant={ButtonVariants.tertiary}
          size={ButtonSizes.sm}
          disabled={step === AddDeclarationStep.upload}
          onClick={handleGoToUpload}
        >
          {t('COMMON_PREVIOUS')}
        </Button>
        <div className={styles.footerRight}>{footerRightButtons}</div>
      </Form.Footer>

      {/* Nothing is saved yet, so the only way out is to drop the picked files. */}
      <ConfirmDialog
        open={isDiscardModalOpen}
        title={t('COMMON_DISCARD_TITLE')}
        bodyTitle={t('COMMON_DISCARD_BODY_TITLE')}
        description={t('COMMON_DISCARD_DESCRIPTION')}
        confirmLabel={t('COMMON_YES')}
        onConfirm={goBack}
        onCancel={handleCancelDiscard}
      />
    </div>
  );
};
